'use strict';

import { assignDeep, Buildings, GSAPAnimationMixer, projectMode, threeJsVariables, TRoot } from '../../../settings';
import { BaseElement } from '../../interfaces/BaseElement';
import { Cloudflare } from '../../api/Cloudflare';
import { getCurrentLang } from '../langSwitcher/langSwitcher';
import { Color, LinearSRGBColorSpace, Vector3 } from 'three';
import gsap from "gsap";
import { Lerp } from '../../projectTime/Lerp';
import { getCurrentCameraDistance } from '../../controls/orbitControls';
import { clamp, userPrevent } from '../../../functions';

const DEFAULT_CONFIG = {
	active: false,
	orbitObjectsToHide:[],
	lutNightLights: {},
	animation: {
		height: 2000
	},
	data: []
};

const _position = new Vector3();
const _target = new Vector3();
const _cameraVector = new Vector3();
const _direction = new Vector3();

export class OrbitBuildingsNavigation {
	constructor () {
		this.config = assignDeep({},DEFAULT_CONFIG,config.features.orbitBuildingsNavigation || {});
		this.container = null;
		this.buttons = {};
		this.activeButton = null;
		this.cache = new Map();
		
		this.#createNavigationBar();
	}
	
	applyLutNightLightsSettings(lut,lutName) {
		if (!this.active)
			return;
		
		const settings = this.config.lutNightLights[lutName];
		if (settings) {
			settings.color = new Color().setStyle(settings.color,LinearSRGBColorSpace);
			lut.applyNightLightsSettings(settings);
		}
	}
	
	objectsVisibility(state) {
		if (!this.active)
			return;
		
		if (Buildings.oneMeshModel) {
			Buildings.oneMeshModel.visible = state;
		} else {
			Buildings.getBuildingsArray().forEach(building => {
				building.modelGroup.visible = state;
			});
		}
		
		this.config.orbitObjectsToHide.forEach(name => {
			const object = threeJsVariables.scene.getObjectByName(name);
			if (object)
				object.visible = state;
		});
	}
	
	#createNavigationBar() {
		if (!this.active)
			return;
		
		this.container = new BaseElement()
			.addClass('orbit-buildings-navigation')
			.appendTo(TRoot)
			.DOMElement;
	}
	
	addBuilding(building) {
		if (!this.active)
			return;
		
		const buttonData = this.config.data.find(btn => btn.buildingEngineId === building.name);
		
		const buildingButton = new BaseElement()
			.addClass('orbit-buildings-navigation__building-btn')
			.event('click',() => this.clickHandler(building,'navigation'))
			.appendTo(this.container)
			.DOMElement;
		
		this.buttons[building.name] = buildingButton;
		
		let logo;
		
		function loadLogo (url) {
			let link = url;
			const image = new Image();
			image.classList.add('orbit-buildings-navigation__building-logo');
			image.classList.add('--building-logo-img');
			const regExp = new RegExp(config.cdnRepository,'g');
			
			if (regExp.test(link)) {
				link = Cloudflare.resize(link,{width: 300});
			}
			
			image.onerror = () => {
				image.src = '/img/icons/poi/POI__PLUG.jpg';
			};
			
			image.src = link;
			
			return image;
		}
		
		if (buttonData?.logo) {
			logo = loadLogo(buttonData.logo);
		} else {
			logo = loadLogo('/img/icons/poi/POI__PLUG.jpg');
		}
		
		buildingButton.appendChild(logo);
		
		const buildingName = new BaseElement()
			.addClass('orbit-buildings-navigation__building-name','language-string')
			.appendTo(buildingButton);
		
		const currentLang = getCurrentLang();
		const names = buttonData?.buttonName?.length ? buttonData.buttonName : building.info.towerName;
		
		names.forEach(data => {
			buildingName.addAttribute(`data-lang-${data.lang}`,data.value);
			if (currentLang === data.lang) {
				buildingName.innerHtml(data.value);
			}
		});
	}
	
	getBuildingLabelName(building) {
		let name = building.info.labelName;
		const label = this.config.data.find(btn => btn.buildingEngineId === building.name);
		if (label && label.buildingLabel?.length) {
			name = label.buildingLabel;
		}
		
		return name;
	}
	
	updateActiveButton() {
		if (!this.active)
			return;
		
		const vec = new Vector3();
		const buildings = Buildings.getBuildingsArray();
		const distances = buildings.map(building => {
			building.selectionsGroup.getWorldPosition(vec);
			return vec.distanceTo(threeJsVariables.orbitControls.target);
		});
		
		const minDistance = Math.min(...distances);
		const index = distances.indexOf(minDistance);
		this.#activateButton(buildings[index].name);
	}
	
	clickHandler(building,fromObject) {
		building.selectionsGroup.getWorldPosition(_position);
		const distance = _position.distanceTo(threeJsVariables.orbitControls.target);
		
		if (distance > 200) {
			this.#flyToBuilding(_position,building);
			this.#activateButton(building.name);
		} else {
			projectMode.Cylinder.enter(building);
		}
		
		// switch (fromObject) {
		// 	case 'navigation':
		// 		if (distance > 200) {
		// 			this.#flyToBuilding(_position);
		// 		break;
		// 	case 'buildingPoi':
		// 		if (distance < 200) {
		// 			projectMode.Cylinder.enter(building);
		// 		} else {
		// 			this.#flyToBuilding(_position);
		// 		}
		// 		break;
		// }
	}
	
	#flyToBuilding(position,building) {
		_position.copy(position);
		const controls = threeJsVariables.orbitControls;
		const trackballControls = threeJsVariables.trackballControls;
		const camera = threeJsVariables.perspectiveCamera;
		const buildingData = this.config.data.find(el => el.buildingEngineId === building.name);
		
		_target.copy(controls.target);
		
		this.cache.clear();
		this.cache.set('minPolarAngle', controls.minPolarAngle);
		this.cache.set('maxPolarAngle', controls.maxPolarAngle);
		this.cache.set('minDistance',trackballControls.minDistance);
		this.cache.set('maxDistance',trackballControls.maxDistance);
		
		userPrevent(true);
		controls.enabled = false;
		trackballControls.enabled = false;
		
		const mixer = GSAPAnimationMixer.registerNewMixer('orbitBuildingsNavigation');
		mixer.addCallback('onStart',() => {
			trackballControls.maxDistance = Infinity;
			controls.minPolarAngle = 0;
		});
		
		mixer.addCallback('onComplete',() => {
			transitionPass.active = false;
			transitionPass.scale = 0;
			transitionPass.opacity = 1;
			controls.minPolarAngle = this.cache.get('minPolarAngle');
			controls.maxPolarAngle = this.cache.get('maxPolarAngle');
			trackballControls.minDistance = this.cache.get('minDistance');
			trackballControls.maxDistance = this.cache.get('maxDistance');
			controls.enabled = true;
			trackballControls.enabled = true;
			userPrevent(false);
		});
		
		const transitionPass = threeJsVariables.composer.getPass('map');
		transitionPass.scale = 0;
		transitionPass.opacity = 1;
		transitionPass.active = true;
		
		const height = this.config.animation.height;
		let time = 0;
		
		const currentPhi = controls.getPolarAngle();
		const currentTheta = controls.getAzimuthalAngle();
		
		const phiLerp = new Lerp(currentPhi,0);
		const currentCameraDistance = getCurrentCameraDistance();
		const zoomDistanceLerp = new Lerp(currentCameraDistance,height);
		const zoomInThetaLerp = new Lerp(currentTheta,buildingData.hasOwnProperty('direction') ? buildingData.direction : currentTheta);
		
		const outProgress = {value: 0};
		const outAnimation = gsap.to(outProgress,{
			value: 1,
			ease: GSAPAnimationMixer.ease,
			duration: GSAPAnimationMixer.duration,
			onUpdate: () => {
				if (outProgress.value < 0.5) {
					transitionPass.opacity = clamp(1 - outProgress.value,0.5,1);
				} else {
					transitionPass.opacity = clamp(outProgress.value,0.5,1);
				}
				controls.setPolarAngle(phiLerp.lerp(outProgress.value));
				_direction.subVectors(camera.position,controls.target);
				_cameraVector.copy(_direction).normalize().setLength(zoomDistanceLerp.lerp(outProgress.value)).add(controls.target);
				camera.position.copy(_cameraVector);
			}
		});
		
		mixer.addAnimation(outAnimation,time);
		time += GSAPAnimationMixer.duration;
		
		const moveAnimation = gsap.to(controls.target,{
			x: _position.x,
			y: _target.y,
			z: _position.z,
			ease: GSAPAnimationMixer.ease,
			duration: GSAPAnimationMixer.duration,
			onUpdate: () => {
				camera.position.x = controls.target.x;
				camera.position.z = controls.target.z;
				controls.setAzimuthAngle(currentTheta);
			}
		});
		
		mixer.addAnimation(moveAnimation,time);
		time += GSAPAnimationMixer.duration;
		
		const inProgress = {value: 1};
		const inAnimation = gsap.to(inProgress,{
			value: 0,
			ease: GSAPAnimationMixer.ease,
			duration: GSAPAnimationMixer.duration,
			onUpdate: () => {
				if (inProgress.value < 0.5) {
					transitionPass.opacity = clamp(1 - inProgress.value,0.5,1);
				} else {
					transitionPass.opacity = clamp(inProgress.value,0.5,1);
				}
				controls.setPolarAngle(phiLerp.lerp(inProgress.value));
				controls.setAzimuthAngle(zoomInThetaLerp.lerp(1 - inProgress.value));
				_direction.subVectors(camera.position,controls.target);
				_cameraVector.copy(_direction).normalize().setLength(zoomDistanceLerp.lerp(inProgress.value)).add(controls.target);
				camera.position.copy(_cameraVector);
			}
		});
		
		mixer.addAnimation(inAnimation,time);
		mixer.play();
	}
	
	#activateButton(buildingEngineId) {
		if (this.activeButton) {
			this.activeButton.classList.remove('--orbit-buildings-navigation-active-button');
		}
		
		this.activeButton = this.buttons[buildingEngineId];
		this.activeButton.classList.add('--orbit-buildings-navigation-active-button');
	}
	
	get active() {
		return this.config.active;
	}
	
}
